import { z } from "zod";
import { zId, zIsoDate, zMoney } from "./common.js";

export const zUpsertMembershipProgram = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().max(5000).optional(),
  fundId: zId,
  campaignId: zId.nullable().optional(),
});
export type UpsertMembershipProgram = z.infer<typeof zUpsertMembershipProgram>;

export const zUpsertMembershipLevel = z.object({
  name: z.string().trim().min(1).max(120),
  price: zMoney,
  /** value of member benefits (dues) — non-deductible; must not exceed price */
  fairMarketValue: zMoney.default(0),
  termMonths: z.number().int().min(1).max(120).default(12),
  sortOrder: z.number().int().min(0).default(0),
  archived: z.boolean().default(false),
});
export type UpsertMembershipLevel = z.infer<typeof zUpsertMembershipLevel>;

/** staff-entered join; payment records a gift with the dues/contribution split */
export const zJoinMembership = z.object({
  constituentId: zId,
  levelId: zId,
  startDate: zIsoDate.optional(),
  /** skip the gift (e.g. comp membership) */
  complimentary: z.boolean().default(false),
});
export type JoinMembership = z.infer<typeof zJoinMembership>;

export const zRenewMembership = z.object({
  /** switch level on renewal (upgrade/downgrade); defaults to the current one */
  levelId: zId.optional(),
  complimentary: z.boolean().default(false),
});
export type RenewMembership = z.infer<typeof zRenewMembership>;

/** persisted: active | cancelled; lapsed is derived from expiresAt */
export const MEMBERSHIP_STATUSES = ["active", "lapsed", "cancelled"] as const;
export const zMembershipStatus = z.enum(MEMBERSHIP_STATUSES);
export type MembershipStatus = z.infer<typeof zMembershipStatus>;

// ---------------------------------------------------------------------------
// CT-RM-17: renewal reminders — a per-program schedule of reminders relative
// to a member's expiry (e.g. 60/30/7 days before, 7 days after a lapse), sent
// by email (through the outbound queue) or raised as a staff task.

export const MEMBERSHIP_REMINDER_TIMINGS = ["before_expiry", "after_lapse"] as const;
export type MembershipReminderTiming = (typeof MEMBERSHIP_REMINDER_TIMINGS)[number];
export const MEMBERSHIP_REMINDER_CHANNELS = ["email", "task"] as const;
export type MembershipReminderChannel = (typeof MEMBERSHIP_REMINDER_CHANNELS)[number];
export const MEMBERSHIP_REMINDER_MAX_RULES = 8;
export const MEMBERSHIP_REMINDER_MAX_DAYS = 365;
/** the last after-lapse reminder in a schedule is still sent if the sweep is up to this many days late */
export const MEMBERSHIP_REMINDER_LAPSE_CATCHUP_DAYS = 30;
export const MEMBERSHIP_REMINDER_DEFAULT_TASK_DUE_DAYS = 3;

/** log statuses: an email handed to the outbound queue, a task raised, or a reminder skipped by a guard (consent, no address) */
export const MEMBERSHIP_REMINDER_STATUSES = ["queued", "task_created", "skipped"] as const;
export type MembershipReminderStatus = (typeof MEMBERSHIP_REMINDER_STATUSES)[number];

export interface MembershipReminderRuleIssue {
  field: "days" | "subject" | "bodyHtml" | "taskTitle";
  message: string;
}

const hasText = (s: string | null | undefined) => (s ?? "").trim().length > 0;
const htmlHasText = (html: string | null | undefined) =>
  !!html && (/<img\b/i.test(html) || html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim().length > 0);

/** Required-field rules per channel, shared by the schedule editor (inline errors) and the API (400). */
export function membershipReminderRuleIssues(rule: {
  timing: MembershipReminderTiming;
  days: number;
  channel: MembershipReminderChannel;
  templateId?: string | null;
  subject?: string | null;
  bodyHtml?: string | null;
  taskTitle?: string | null;
}): MembershipReminderRuleIssue[] {
  const out: MembershipReminderRuleIssue[] = [];
  if (rule.timing === "after_lapse" && rule.days < 1) out.push({ field: "days", message: "A lapse reminder is at least 1 day after expiry" });
  if (rule.channel === "email" && !rule.templateId) {
    if (!hasText(rule.subject)) out.push({ field: "subject", message: "Add a subject, or choose a template" });
    if (!htmlHasText(rule.bodyHtml)) out.push({ field: "bodyHtml", message: "Write a message, or choose a template" });
  }
  if (rule.channel === "task" && !hasText(rule.taskTitle)) out.push({ field: "taskTitle", message: "Give the task a title" });
  return out;
}

export const zMembershipReminderRule = z
  .object({
    timing: z.enum(MEMBERSHIP_REMINDER_TIMINGS),
    /** days before expiry (0 = on the expiry date) or days after the lapse (1+) */
    days: z.number().int().min(0).max(MEMBERSHIP_REMINDER_MAX_DAYS),
    channel: z.enum(MEMBERSHIP_REMINDER_CHANNELS),
    enabled: z.boolean().default(true),
    // email: a saved template, or an inline subject + body
    templateId: zId.nullable().optional(),
    subject: z.string().max(300).optional(),
    bodyHtml: z.string().max(50_000).optional(),
    // task
    taskTitle: z.string().max(300).optional(),
    assigneeId: zId.nullable().optional(),
    taskDueDays: z.number().int().min(0).max(MEMBERSHIP_REMINDER_MAX_DAYS).default(MEMBERSHIP_REMINDER_DEFAULT_TASK_DUE_DAYS),
  })
  .superRefine((rule, ctx) => {
    for (const issue of membershipReminderRuleIssues(rule)) ctx.addIssue({ code: "custom", path: [issue.field], message: issue.message });
  });
export type MembershipReminderRuleInput = z.input<typeof zMembershipReminderRule>;
export type MembershipReminderRuleDraft = z.infer<typeof zMembershipReminderRule>;

export function describeMembershipReminder(r: { timing: MembershipReminderTiming; days: number }): string {
  if (r.timing === "before_expiry") return r.days === 0 ? "on the expiry date" : `${r.days} day${r.days === 1 ? "" : "s"} before expiry`;
  return `${r.days} day${r.days === 1 ? "" : "s"} after lapse`;
}

export const zSaveMembershipReminders = z
  .object({
    /** published page with a membership form for this program — the {{renewal_link}} target */
    renewalPageId: zId.nullable().default(null),
    rules: z.array(zMembershipReminderRule).max(MEMBERSHIP_REMINDER_MAX_RULES),
  })
  .superRefine((body, ctx) => {
    const seen = new Set<string>();
    body.rules.forEach((r, i) => {
      const key = `${r.timing}:${r.days}`;
      if (seen.has(key)) ctx.addIssue({ code: "custom", path: ["rules", i, "days"], message: `Two reminders ${describeMembershipReminder(r)}` });
      seen.add(key);
    });
  });
export type SaveMembershipReminders = z.infer<typeof zSaveMembershipReminders>;

/** 60/30/7 days before expiry, plus a nudge 7 days after a lapse */
export const SUGGESTED_MEMBERSHIP_REMINDER_SCHEDULE: { timing: MembershipReminderTiming; days: number }[] = [
  { timing: "before_expiry", days: 60 },
  { timing: "before_expiry", days: 30 },
  { timing: "before_expiry", days: 7 },
  { timing: "after_lapse", days: 7 },
];

/** position on a timeline relative to the expiry date (negative = before) */
export const membershipReminderOffset = (r: { timing: MembershipReminderTiming; days: number }) => (r.timing === "before_expiry" ? -r.days : r.days);

/** whole days from `from` to `to` (YYYY-MM-DD) */
export function daysBetweenIsoDates(from: string, to: string): number {
  return Math.round((Date.parse(`${to.slice(0, 10)}T00:00:00Z`) - Date.parse(`${from.slice(0, 10)}T00:00:00Z`)) / 86_400_000);
}

export function addDaysIso(isoDate: string, days: number): string {
  return new Date(Date.parse(`${isoDate.slice(0, 10)}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

/**
 * Which reminder of a schedule is due for a membership on `today`, if any.
 *
 * Each enabled reminder owns a window on the expiry timeline: it opens on its
 * own day and closes when the next reminder opens. A before-expiry window also
 * closes at the lapse (the day after expiry), and the last after-lapse window
 * closes MEMBERSHIP_REMINDER_LAPSE_CATCHUP_DAYS later. So a sweep that missed
 * a day still sends the right reminder, a member is never sent a stale one (no
 * "60 days to go" a week before expiry), and at most one reminder is due per
 * membership per day. The sender records each (membership, window, term) so
 * it never goes out twice.
 */
export function dueMembershipReminder<R extends { timing: MembershipReminderTiming; days: number; enabled?: boolean }>(
  rules: readonly R[],
  expiresAt: string,
  today: string,
): { rule: R; dueDate: string } | null {
  const ordered = rules.filter((r) => r.enabled !== false).sort((a, b) => membershipReminderOffset(a) - membershipReminderOffset(b));
  const d = daysBetweenIsoDates(expiresAt, today);
  for (let i = 0; i < ordered.length; i++) {
    const rule = ordered[i]!;
    const open = membershipReminderOffset(rule);
    const next = ordered[i + 1];
    let close = next ? membershipReminderOffset(next) : open + MEMBERSHIP_REMINDER_LAPSE_CATCHUP_DAYS;
    if (rule.timing === "before_expiry") close = Math.min(close, 1);
    if (d >= open && d < close) return { rule, dueDate: addDaysIso(expiresAt, open) };
  }
  return null;
}

/** Merge fields a renewal reminder adds to the automation set (same `{{token}}` syntax and renderer). */
export const MEMBERSHIP_REMINDER_MERGE_FIELDS = [
  { key: "program_name", token: "{{program_name}}", label: "Program", sample: "Friends of the River" },
  { key: "level_name", token: "{{level_name}}", label: "Membership level", sample: "Family" },
  { key: "expiry_date", token: "{{expiry_date}}", label: "Expiry date", sample: "December 31, 2026" },
  { key: "renewal_link", token: "{{renewal_link}}", label: "Personal renewal link", sample: "https://example.org/p/riverside/renew?d=sample" },
] as const;
export type MembershipReminderMergeFieldKey = (typeof MEMBERSHIP_REMINDER_MERGE_FIELDS)[number]["key"];

export interface MembershipReminderRule extends MembershipReminderRuleDraft {
  id: string;
}

export interface MembershipReminderSettings {
  programId: string;
  renewalPageId: string | null;
  rules: MembershipReminderRule[];
  /** choices for the editor (staff with membership:manage may not hold email permissions) */
  templates: { id: string; name: string; subject: string }[];
  /** published pages whose form is a membership form for this program */
  renewalPages: { id: string; name: string; slug: string }[];
}

export interface MembershipReminderLogEntry {
  id: string;
  timing: MembershipReminderTiming;
  days: number;
  channel: MembershipReminderChannel;
  status: MembershipReminderStatus;
  detail: string | null;
  toAddress: string | null;
  /** the term the reminder was for (the expiry date at the time) */
  termExpiresAt: string;
  dueDate: string;
  /** live state of the queued email (queued, sent, suppressed…) */
  emailStatus: string | null;
  taskId: string | null;
  taskStatus: string | null;
  createdAt: string;
}

/** preview / "send test to me": rendered only — never queued or sent */
export const zMembershipReminderPreview = z.object({
  rule: zMembershipReminderRule,
  /** render for a real member (their merge values and consent); omitted = sample values */
  membershipId: zId.optional(),
});
export type MembershipReminderPreviewInput = z.infer<typeof zMembershipReminderPreview>;

export interface MembershipReminderPreview {
  channel: MembershipReminderChannel;
  /** recipient the reminder would go to (the staff user's own address for a test) */
  to: string | null;
  subject: string | null;
  html: string | null;
  taskTitle: string | null;
  /** why the live sweep would skip this member (consent, no address), if it would */
  wouldSkip: string | null;
  /** always false: preview and test only render */
  sent: false;
}

export interface MembershipReminderSweepResult {
  queued: number;
  tasks: number;
  skipped: number;
}

/** CT-RM-17: a known member arriving on a membership form through their signed ?d= link */
export interface PublicMembershipRenewal {
  /** the level-select option to preselect (their current level, if still offered) */
  levelOption: string | null;
  expiresAt: string;
  lapsed: boolean;
}
